import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import
{
  Button,
  Icon,
  Container,
  Text,
  Input,
  Slider,
  FormAlert,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import api from '../utils/axios';
import { validatePassword } from '../utils/passwordValidation';
import { isValidEmail } from '../utils/validators';
import { useTurnstile } from '../hooks/useTurnstile';

interface SignupData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
}

export const SignupPage: React.FC = () => {
  const [formData, setFormData] = useState<SignupData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    organizationName: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Field touched states for validation
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [organizationNameTouched, setOrganizationNameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const navigate = useNavigate();
  const errorRef = useRef<HTMLDivElement>(null);
  const { turnstileRef, token: turnstileToken, isReady: turnstileReady, isAvailable: turnstileAvailable, resetTurnstile } = useTurnstile({ action: 'signup' });

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Get field error messages
  const getFirstNameError = (): string => {
    if (!firstNameTouched) return '';
    if (!formData.firstName) return 'First name is required';
    if (!formData.firstName.trim()) return 'First name cannot be blank';
    if (formData.firstName.trim().length > 255) return 'First name is too long (max 255 characters)';
    return '';
  };

  const getLastNameError = (): string => {
    if (!lastNameTouched) return '';
    if (!formData.lastName) return 'Last name is required';
    if (!formData.lastName.trim()) return 'Last name cannot be blank';
    if (formData.lastName.trim().length > 255) return 'Last name is too long (max 255 characters)';
    return '';
  };

  const getEmailError = (): string => {
    if (!emailTouched) return '';
    if (!formData.email) return 'Email is required';
    if (!isValidEmail(formData.email)) return 'Please enter a valid email address';
    return '';
  };

  const getPasswordError = (): string => {
    if (!passwordTouched) return '';
    if (!formData.password) return 'Password is required';
    const validation = validatePassword(formData.password);
    if (!validation.isValid) return validation.errors[0];
    return '';
  };

  const getConfirmPasswordError = (): string => {
    if (!confirmPasswordTouched) return '';
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== formData.password) return 'Passwords do not match';
    return '';
  };

  const getOrganizationNameError = (): string => {
    if (!organizationNameTouched) return '';
    const trimmed = formData.organizationName?.trim() ?? '';
    if (trimmed.length > 255) return 'Organization name is too long (max 255 characters)';
    return '';
  };

  // Check if form has any errors or empty required fields
  const hasFormErrors = (): boolean => {
    const validation = validatePassword(formData.password);
    const orgTrimmed = formData.organizationName?.trim() ?? '';
    return (
      !formData.firstName ||
      !formData.firstName.trim() ||
      formData.firstName.trim().length > 255 ||
      !formData.lastName ||
      !formData.lastName.trim() ||
      formData.lastName.trim().length > 255 ||
      !formData.email ||
      !isValidEmail(formData.email) ||
      orgTrimmed.length > 255 ||
      !formData.password ||
      !validation.isValid ||
      !confirmPassword ||
      confirmPassword !== formData.password
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');

    // Mark password as touched when user starts typing
    if (name === 'password') {
      setPasswordTouched(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Mark all fields as touched
    setFirstNameTouched(true);
    setLastNameTouched(true);
    setEmailTouched(true);
    setOrganizationNameTouched(true);
    setPasswordTouched(true);
    setConfirmPasswordTouched(true);

    // Validation
    if (!formData.firstName?.trim()) {
      setError('First name is required');
      setIsLoading(false);
      return;
    }

    if (!formData.lastName?.trim()) {
      setError('Last name is required');
      setIsLoading(false);
      return;
    }

    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      setIsLoading(false);
      return;
    }

    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password using the password policy
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/signup', {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        organizationName: formData.organizationName?.trim() || undefined,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      });

      if (response.data.message) {
        // Clear form
        setFormData({
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          organizationName: '',
        });
        setConfirmPassword('');

        // Redirect to email verification
        navigate('/verify-email', {
          state: {
            email: response.data.email || formData.email,
            message: response.data.message,
          },
        });
      }
    } catch (error: any) {
      let errMsg = 'Registration failed. Please try again.';

      if (error.response?.data?.detail) {
        errMsg = error.response.data.detail;
      } else if (error.message) {
        errMsg = error.message;
      }

      setError(errMsg);
    } finally {
      setIsLoading(false);
      resetTurnstile();
    }
  };

  const handleDismissError = () => {
    setError('');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      <div className="hidden md:block col md:h-screen md:sticky md:top-0">
        <Container padding={'4xl'} className="h-full flex items-center justify-center bg-bg-secondary-alt">
          <Slider autoPlayInterval={5000} />
        </Container>
      </div>
      <div className="col md:h-screen md:overflow-y-auto">
        <div className="bg-bg-secondary-alt">
          <Container padding={'xl'} className="min-h-screen flex items-center justify-center py-8 bg-bg-tertiary">
            <Container padding={'4xl'} className="w-full max-w-md flex flex-col gap-bg-v2-xl rounded-bg-v2-md container-shadow-lg hover-scale-sm page-transition-enter">
              <div className="flex items-center justify-center gap-bg-v2-md animate-initial-hidden animate-fade-in-down animate-delay-200">
                <Icon name={IconName.BrandLogo} />
                <span
                  className="text-text-brand-primary font-semibold"
                  style={{
                    fontFamily: 'Inter',
                    fontSize: 'var(--font-size-display-sm, 30px)',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: 'var(--line-height-display-lg, 60px)',
                  }}
                >
                  BlueGenAI
                </span>
              </div>
              <Text variant={'display-xs'} weight="medium" className="font-bg-v2-display animate-initial-hidden animate-fade-in-up animate-delay-300" color="primary" align="center" as="p">
                The fastest way to build applications
              </Text>
              <div ref={errorRef}>
                <FormAlert
                  message={error}
                  variant="error"
                  onDismiss={handleDismissError}
                />
              </div>
              <form className="flex flex-col w-full gap-bg-v2-2xl" onSubmit={handleSubmit} noValidate>
                <Text variant={'text-lg'} color="secondary" className={'font-bg-v2-display'} weight={'semibold'} as="h2">
                  Sign up
                </Text>
                {/* First Name and Last Name */}
                <div className="grid grid-cols-2 gap-bg-v2-lg">
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    label="First Name"
                    placeholder="Enter first name"
                    required
                    errorText={getFirstNameError()}
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onBlur={() => setFirstNameTouched(true)}
                  />
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    label="Last Name"
                    placeholder="Enter last name"
                    required
                    errorText={getLastNameError()}
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onBlur={() => setLastNameTouched(true)}
                  />
                </div>

                {/* Email */}
                <Input
                  id="email"
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="Enter your email address"
                  required
                  errorText={getEmailError()}
                  startIcon={<Icon name={IconName.Mail} size="md" />}
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => setEmailTouched(true)}
                />

                {/* Company/Organization */}
                <Input
                  id="organizationName"
                  name="organizationName"
                  type="text"
                  label="Company/Organization (Optional)"
                  placeholder="Enter company/organization name"
                  errorText={getOrganizationNameError()}
                  startIcon={<Icon name={IconName.Organization} size="md" />}
                  value={formData.organizationName}
                  onChange={handleInputChange}
                  onBlur={() => setOrganizationNameTouched(true)}
                />

                {/* Password */}
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  placeholder="Enter your password"
                  required
                  errorText={getPasswordError()}
                  startIcon={<Icon name={IconName.Lock} size="md" />}
                  endIcon={!getPasswordError() && <Icon name={showPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
                  onEndIconClick={() => setShowPassword(!showPassword)}
                  value={formData.password}
                  onChange={handleInputChange}
                  onBlur={() => setPasswordTouched(true)}
                  showStrength={true}
                />

                {/* Confirm Password */}
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  placeholder="Re-enter your password"
                  required
                  errorText={getConfirmPasswordError()}
                  startIcon={<Icon name={IconName.Lock} size="md" />}
                  endIcon={!getConfirmPasswordError() && <Icon name={showConfirmPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
                  onEndIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setConfirmPasswordTouched(true);
                    setError('');
                  }}
                  onBlur={() => setConfirmPasswordTouched(true)}
                />
                <div ref={turnstileRef} />
                <div>
                  <Button
                    variant={'primary'}
                    type={'submit'}
                    loading={isLoading || !turnstileReady}
                    disabled={isLoading || hasFormErrors() || !turnstileReady || (turnstileAvailable && !turnstileToken)}
                    className={'w-full flex justify-center items-center hover-lift'}
                    label={'Create Account'}
                  />
                </div>
                <div>
                  <Text variant={'text-sm'} color="primary" align="center" className={'font-bg-v2-sans'} weight={'regular'} as="p">
                    {`By continuing, you agree to BlueGenAI's`}
                  </Text>
                  <Text variant={'text-sm'} color="primary" align="center" className={'font-bg-v2-sans'} weight={'regular'} as="p">
                    <a href="https://policies.bluegenai.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="font-bg-v2-sans font-bg-v2-medium text-bg-brand-solid hover:opacity-80 transition-smooth">Terms of Service</a> and <a href="https://policies.bluegenai.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-bg-v2-sans font-bg-v2-medium text-bg-brand-solid hover:opacity-80 transition-smooth">Privacy Policy</a>
                  </Text>
                </div>
                <div>
                  <Text variant={'text-sm'} color="secondary" align="center" className={'font-bg-v2-sans'} weight={'regular'} as="p">
                    Already have an account?
                    <Link to="/login" className="font-bg-v2-sans font-bg-v2-medium text-bg-brand-solid hover:opacity-80 px-bg-v2-sm transition-smooth">
                      Sign in
                    </Link>
                  </Text>
                </div>
              </form>
            </Container>
          </Container>
        </div>
      </div>
    </div>
  );
};
